import { useContext, useState } from "react"
import { Link } from "react-router-dom"
import { useClerk } from "@clerk/react"
import { useMeStore, type MeEntitlements, type MeMetric } from "@/features/auth/hooks/useCurrentUser"
import { useSubscription } from "@/features/billing/components/SubscriptionPanel"
import { shortDate } from "@/features/billing/components/BillingBanners"
import { PRO_FALLBACK_LINES, allowanceLines, metricLabel, planName, plusLines } from "@/features/billing/copy"
import { FALLBACK_ADDONS, planOf, priceCopy, rupees } from "@/features/billing/plan"
import { apiClient } from "@/services/api.client"
import { track } from "@/lib/analytics"
import { AuthContext } from "@/providers/AuthProvider"
import { NiraBar } from "../ds/NiraBar"
import { PlanCard, planLine } from "../ds/PlanCard"
import { usePageTitle } from "../answer/hooks"
import "../answer/answer.css"
import "../answer/answer-v4.css"
import "../case/case.css"
import "./account.css"

/**
 * The account, in Nira's own pages (ported from the platform's profile menu and /subscription,
 * which draw the platform's older shell): who is signed in, the plan, what has been used, and the
 * way to Clerk's own account screens for name, email, password and sessions. Billing itself is the
 * platform's (features/billing): these pages only draw it.
 */

const WINDOW_WORDS: Record<string, string> = { day: " today", month: " this month", draft: " in this draft" }

/** How a used allowance reads: "1 of 1, for good", "2 of 3 today", "4 of 40 this month". */
function usedLine(m: MeMetric): string {
  if (m.limit === null) return `${m.used} used, no limit`
  return `${m.used} of ${m.limit}${WINDOW_WORDS[m.window] ?? ", for good"}`
}

/** "Resets 1 November" under a used allowance, for the windows that reset. */
function resetsLine(m: MeMetric): string | null {
  if (m.window !== "day" && m.window !== "month") return null
  const d = shortDate(m.resets_at)
  return d ? `Resets ${d}` : m.window === "day" ? "Resets at midnight" : null
}

const addonName = (sku: string): string => {
  const a = FALLBACK_ADDONS.find((x) => x.sku === sku)
  return a?.metric === "order.draft_edit" ? `${a.quantity} more draft edits` : a ? "Extra order draft" : sku
}

/** The renewal: when it was charged, when it is next, and the way to pay a failed one. */
function RenewalRows({ e }: { e: MeEntitlements }) {
  const r = e.renewal
  if (!r || !e.subscribed) return null
  const last = shortDate(r.last_charged_at)
  const next = shortDate(r.next_charge_at)
  const grace = e.plan_state === "grace"
  return (
    <>
      {last ? <div><dt>Last charged</dt><dd>{r.last_amount_minor != null ? `${rupees(r.last_amount_minor)} on ${last}` : last}</dd></div> : null}
      {next && !grace ? <div><dt>Next charge</dt><dd>{next}</dd></div> : null}
      {grace ? (
        <div>
          <dt>Payment</dt>
          <dd>
            Didn&apos;t go through. {shortDate(e.grace_ends_at) ? `Pay by ${shortDate(e.grace_ends_at)}. ` : ""}
            {r.pay_url ? <a className="npro-link" href={r.pay_url}>Pay now</a> : null}
          </dd>
        </div>
      ) : null}
    </>
  )
}

function useSignedIn() {
  const auth = useContext(AuthContext)
  const user = auth?.session?.user
  return { user, logout: auth?.logout }
}

/** "Delete my account": a confirm step, then the server deletes the content and the sign-in account, and the reader is signed out. */
function DeleteAccount({ logout }: { logout?: () => Promise<void> | void }) {
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState("")
  const remove = async () => {
    setBusy(true); setMessage("")
    try {
      await apiClient.post<unknown>("/api/v1/me/delete-account", { confirm: true })
      try { await logout?.() } catch { /* the sign-in account is already gone */ }
      window.location.assign("/")
    } catch (err) {
      const text = JSON.stringify(err instanceof Error ? { m: err.message, b: (err as { body?: unknown }).body } : err)
      setMessage(text.includes("active_subscription")
        ? "Cancel your subscription first (Manage subscription above); it must not renew. Then delete your account."
        : "Couldn't delete the account. Nothing more was lost; try again, or write to contact@praxeumai.com.")
      setBusy(false)
    }
  }
  return (
    <section className="nac-sec" aria-labelledby="nac-delete">
      <h2 id="nac-delete">Delete my account</h2>
      <p className="nac-note">Deletes your workspaces, documents, questions, answers, shares and feedback, and your sign-in account. Copies are erased from our systems within 30 days (AI debugging traces within 60). Payment records are kept as the law requires. This can&apos;t be undone.</p>
      {message ? <p className="nac-note" role="alert">{message}</p> : null}
      {confirming ? (
        <div className="nac-confirm" role="alertdialog" aria-labelledby="nac-delete-t">
          <h3 id="nac-delete-t">Delete your account and everything in it?</h3>
          <p className="nac-acts">
            <button type="button" className="ncf-btn" disabled={busy} onClick={() => setConfirming(false)}>Keep my account</button>
            <button type="button" className="ncf-btn danger" disabled={busy} aria-busy={busy} onClick={() => void remove()}>{busy ? "Deleting…" : "Yes, delete my account"}</button>
          </p>
        </div>
      ) : (
        <p className="nac-acts"><button type="button" className="ncf-btn danger" onClick={() => setConfirming(true)}>Delete my account</button></p>
      )}
    </section>
  )
}

/** /account, "Profile" in the avatar menu: who you are, your plan, what you have used. */
export function AccountPage() {
  usePageTitle("Profile")
  const { user, logout } = useSignedIn()
  const me = useMeStore((s) => s.me)
  const clerk = useClerk() as ReturnType<typeof useClerk> & { openUserProfile?: () => void }
  const e = me?.entitlements ?? null
  const used = (e?.metrics ?? []).filter((m) => metricLabel(m.metric) !== "allowance")
  const name = me?.display_name || user?.displayName || ""
  const email = me?.email || user?.email || ""
  return (
    <div className="na2 na4 ncf nac">
      <NiraBar crumbs={[{ label: "Home", to: "/ask" }, { label: "Profile" }]} />
      <main className="ncf-main nac-main">
        <header className="ncf-head">
          <div className="ncf-title"><p className="ncf-eyebrow">Profile</p><h1>{name || email || "Account"}</h1></div>
        </header>

        <section className="nac-sec" aria-labelledby="nac-profile">
          <h2 id="nac-profile">Profile</h2>
          <dl className="nac-rows">
            {name ? <div><dt>Name</dt><dd>{name}</dd></div> : null}
            {email ? <div><dt>Email</dt><dd>{email}</dd></div> : null}
          </dl>
          {clerk.openUserProfile ? (
            <p className="nac-acts">
              <button type="button" className="ncf-btn" onClick={() => { track("manage_account_opened"); clerk.openUserProfile?.() }}>Manage your account</button>
              <span className="nac-note">Name, email, password, connected accounts and signed-in devices.</span>
            </p>
          ) : null}
        </section>

        <section className="nac-sec" aria-labelledby="nac-plan">
          <h2 id="nac-plan">Plan</h2>
          <dl className="nac-rows">
            <div><dt>Plan</dt><dd>{e?.exempt ? "Unlimited (staff)" : planName(e?.plan_state, e?.subscribed)}</dd></div>
            {e?.plan_state === "free_month" && shortDate(e.first_month_ends_at) ? <div><dt>First month ends</dt><dd>{shortDate(e.first_month_ends_at)}</dd></div> : null}
            {e ? <RenewalRows e={e} /> : null}
          </dl>
          <p className="nac-acts">
            <Link className="ncf-btn" to="/account/subscription">{e?.subscribed ? "Manage subscription" : "See plans"}</Link>
          </p>
        </section>

        {used.length && !e?.exempt ? (
          <section className="nac-sec" aria-labelledby="nac-used">
            <h2 id="nac-used">What you have used</h2>
            <dl className="nac-rows">
              {used.map((m) => (
                <div key={m.metric}>
                  <dt>{metricLabel(m.metric).replace(/^./, (c) => c.toUpperCase())}</dt>
                  <dd className="mono">{usedLine(m)}{resetsLine(m) ? <span className="nac-note"> · {resetsLine(m)}</span> : null}</dd>
                </div>
              ))}
            </dl>
          </section>
        ) : null}

        {e?.addon_purchases?.length ? (
          <section className="nac-sec" aria-labelledby="nac-addons">
            <h2 id="nac-addons">Add-ons bought</h2>
            <dl className="nac-rows">
              {e.addon_purchases.map((a, i) => (
                <div key={`${a.sku}-${a.purchased_at ?? i}`}><dt>{shortDate(a.purchased_at) ?? "Add-on"}</dt><dd>{addonName(a.sku)}, {rupees(a.price_minor)}</dd></div>
              ))}
            </dl>
          </section>
        ) : null}

        <DeleteAccount logout={logout} />

        {logout && email && !email.endsWith(".local") ? (
          <p><button type="button" className="ncf-btn" onClick={() => void logout()}>Sign out</button></p>
        ) : null}
      </main>
    </div>
  )
}

/** /account/subscription: the plan, renewal, subscribe and cancel. Where billing is not switched on
 * (a local run, or a deployment without Razorpay), it says so and calls nothing. */
export function SubscriptionView() {
  usePageTitle("Subscription")
  const billing = useMeStore((s) => s.me?.billing_enabled === true)
  if (!billing) {
    return (
      <div className="na2 na4 ncf nac">
        <NiraBar crumbs={[{ label: "Home", to: "/ask" }, { label: "Profile", to: "/account" }, { label: "Subscription" }]} />
        <main className="ncf-main nac-main">
          <header className="ncf-head">
            <div className="ncf-title"><p className="ncf-eyebrow">Nira Pro</p><h1>Subscription</h1></div>
          </header>
          <section className="nac-sec" aria-label="Plan">
            <p className="nac-note" role="status">Subscriptions are not switched on here, so there is nothing to subscribe to or cancel on this server.</p>
          </section>
        </main>
      </div>
    )
  }
  return <SubscriptionBody />
}

/** "₹199/month (incl. GST)" -> ["₹199", "/month (incl. GST)"]. */
function splitPrice(price: string): [string, string] {
  const i = price.indexOf("/")
  return i < 0 ? [price, ""] : [price.slice(0, i), price.slice(i)]
}

function SubscriptionBody() {
  const me = useMeStore((s) => s.me)
  const s = useSubscription()
  const e = me?.entitlements ?? null
  const subscribed = s.state?.subscribed === true
  const code = e?.plan_code === "plus" ? "plus" : "pro"
  const plusPlan = planOf(s.plans, "plus")
  const proLines = (allowanceLines(e, s.plan).length && s.plan?.allowances.length ? allowanceLines(e, s.plan) : PRO_FALLBACK_LINES).map(planLine)
  const [proAmount, proPer] = splitPrice(priceCopy(s.plan, "pro"))
  const [plusAmount, plusPer] = splitPrice(priceCopy(plusPlan, "plus"))
  const start = (plan: "plus" | "pro", label: string) => (
    <button type="button" className="ncf-btn primary nst-tier-cta" disabled={s.busy} aria-busy={s.pending === "subscribe"}
      onClick={() => { track("subscribe_clicked", { from: "subscription_page", plan }); void s.subscribe(plan) }}>
      {s.pending === "subscribe" ? "Opening checkout…" : label}
    </button>
  )
  const plusCard = (
    <PlanCard as="section" headingId="nac-plus" eyebrow="Nira Plus" name={subscribed && code === "plus" ? "What your plan gives" : "Nira Plus"}
      price={<div className="nst-price per">{plusAmount}{plusPer ? <span>{plusPer}</span> : null}</div>}
      lines={plusLines(plusPlan).map(planLine)}
      action={subscribed || s.loading ? undefined : start("plus", "Start Plus")} />
  )
  const proCard = (
    <PlanCard as="section" headingId="nac-pro" tone="featured" eyebrow="Nira Pro" name={subscribed && code === "pro" ? "What your plan gives" : "Nira Pro"}
      price={<div className="nst-price per">{proAmount}{proPer ? <span>{proPer}</span> : null}</div>}
      lines={proLines}
      action={s.loading || (subscribed && code === "pro") ? undefined : start("pro", subscribed ? "Upgrade to Pro" : "Start Pro")}
      note={subscribed && code === "plus" ? `This month's ${rupees(plusPlan?.price_minor ?? 19900)} is credited.` : undefined} />
  )
  return (
    <div className="na2 na4 ncf nac">
      <NiraBar crumbs={[{ label: "Home", to: "/ask" }, { label: "Profile", to: "/account" }, { label: "Subscription" }]} />
      <main className="ncf-main nac-main">
        <header className="ncf-head">
          <div className="ncf-title"><p className="ncf-eyebrow">Plans</p><h1>Subscription</h1></div>
        </header>

        <section className="nac-sec" aria-label="Plan">
          {s.loading ? <p className="ncf-msg" role="status">Loading…</p> : (
            <dl className="nac-rows">
              <div><dt>Plan</dt><dd>{subscribed ? s.planLabel : "Free"}</dd></div>
              <div><dt>Status</dt><dd>{s.cancelled ? "Not subscribed" : s.status}</dd></div>
              {subscribed ? <div><dt>Price</dt><dd className="mono">{s.price}</dd></div> : null}
              {s.end && subscribed && !s.cancelling ? <div><dt>Renews on</dt><dd>{s.end}</dd></div> : null}
              {s.end && s.cancelled ? <div><dt>Access until</dt><dd>{s.end}</dd></div> : null}
              {e ? <RenewalRows e={e} /> : null}
            </dl>
          )}
          {s.cancelling ? (
            <p className="nac-note">
              {s.end ? `Your subscription ends on ${s.end}. ` : "Your subscription ends at the end of the current period. "}
              You keep access until then, and it will not renew.
            </p>
          ) : null}
          {s.message ? <p className="nac-note" role="alert">{s.message}</p> : null}

          {subscribed ? (
            s.cancelling ? null : s.confirming ? (
              <div className="nac-confirm" role="alertdialog" aria-labelledby="nac-cancel-t" aria-describedby="nac-cancel-d">
                <h3 id="nac-cancel-t">Cancel your subscription?</h3>
                <p id="nac-cancel-d" className="nac-note">
                  {s.end ? `You keep access until ${s.end}. It will not renew after that.` : "You keep access until the end of the current period. It will not renew after that."}
                </p>
                <p className="nac-acts">
                  <button type="button" className="ncf-btn" disabled={s.busy} onClick={() => s.setConfirming(false)}>Keep subscription</button>
                  <button type="button" className="ncf-btn danger" disabled={s.busy} aria-busy={s.pending === "cancel"} onClick={() => { track("subscription_cancel_confirmed"); void s.cancel() }}>
                    {s.pending === "cancel" ? "Cancelling…" : "Yes, cancel"}
                  </button>
                </p>
              </div>
            ) : (
              <p className="nac-acts"><button type="button" className="ncf-btn" disabled={s.busy} onClick={() => s.setConfirming(true)}>Cancel subscription</button></p>
            )
          ) : null}
        </section>

        {code === "plus" || !subscribed ? plusCard : null}
        {proCard}
      </main>
    </div>
  )
}
